// Reading the 24h heartbeat sparkline. Data access lives in `libs/fleetStatus.js`
// — `heartbeatHistory` ships as a field on the getFleetStatus card.
//
// A bucket is a gap ratio, not a boolean: the longest heartbeat gap overlapping
// that cell divided by `expectedCadenceSeconds`. 1.0 = on rhythm, 3.0 = 15 min
// silent (the stall threshold), 288.0 = silent all window. Unbounded above.
//
// Never hardcode 288 cells or 300s buckets — both ship in the payload so the
// backend can retune the window without a frontend release.

/**
 * @typedef {Object} HeartbeatHistory
 * @property {number} startTs                Epoch SECONDS (UTC) of buckets[0].
 * @property {number} bucketSeconds          Width of each bucket in seconds.
 * @property {number} expectedCadenceSeconds Healthy spacing; the ratio denominator.
 * @property {number[]} buckets              Gap ratios, index 0 = oldest.
 * @property {number} coverage               Received / expected heartbeats, 0..1.
 * @property {number} longestGapSeconds      Worst single silence in the window.
 * @property {number} longGapCount           Stalls. The UI calls these "dropouts".
 * @property {number} sparklineComputedAt    Epoch seconds of the last recompute.
 */

// Mirrors the backend's STALE_GAP_SECONDS (3 × cadence), which mirrors the health
// evaluator's ALIVE_THRESHOLD — so a red cell always agrees with a DOWN pill.
export const DOWN_GAP_RATIO = 3;

/** `>=`, not `>`: 900s / 300s is exactly 3.0 and the contract calls that a stall. */
export function isBucketDown(ratio) {
  return Number.isFinite(ratio) && ratio >= DOWN_GAP_RATIO;
}

// Three missed 15-minute cron cycles — past this the card may not describe the
// fridge any more, so don't present it as live.
export const SPARKLINE_STALE_SECONDS = 45 * 60;

/** True when a history is old enough that it shouldn't be presented as live. */
export function isHistoryStale(history, nowSeconds) {
  if (!history?.sparklineComputedAt) return false;
  return nowSeconds - history.sparklineComputedAt > SPARKLINE_STALE_SECONDS;
}

/** Epoch SECONDS at the start of bucket `i`. */
export function bucketStartSeconds(history, i) {
  return history.startTs + i * history.bucketSeconds;
}

/**
 * Epoch SECONDS at the right edge of the window. Equals `sparklineComputedAt`,
 * but derived from the geometry so the label can't disagree with the pixels.
 * Not "now" — it trails the wall clock by at least the 15-minute cron.
 */
export function historyEndSeconds(history) {
  return history.startTs + history.buckets.length * history.bucketSeconds;
}

/** Axis-edge label: "Tue 12:31 PM". Weekday included — a 24h window starts and
 *  ends at the same clock time, so bare times read as a typo. */
export function formatEdge(epochSeconds) {
  return new Date(epochSeconds * 1000).toLocaleString([], {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** The silence a bucket encodes, in seconds — the ratio back in real units. */
export function bucketGapSeconds(history, i) {
  return Math.round(history.buckets[i] * history.expectedCadenceSeconds);
}

/** Compact duration for tooltips: 45s / 20m / 3h 5m / 2d 4h. */
export function formatGap(seconds) {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rem = minutes % 60;
    return rem ? `${hours}h ${rem}m` : `${hours}h`;
  }
  const days = Math.floor(hours / 24);
  const rem = hours % 24;
  return rem ? `${days}d ${rem}h` : `${days}d`;
}
